import type { TransportListItem } from '@proerp/shared';

/**
 * Termin-Logik für das Dashboard (VB-26004). Rein und ohne React, damit die
 * Zeitgrenzen testbar sind — bei Datumsarithmetik ist genau das die Fehlerquelle.
 *
 * Alle Grenzen liegen in **lokaler** Zeit: „heute" ist der Tag, den der Disponent
 * auf seiner Uhr sieht, nicht der UTC-Tag. In der Datenbank steht UTC (§6), die
 * Umrechnung passiert hier.
 */

/** Ende des lokalen Tages als ISO-Zeitpunkt — obere Grenze der Abfrage. */
export function endOfLocalDay(now: Date): string {
  const end = new Date(now);
  end.setHours(23, 59, 59, 999);
  return end.toISOString();
}

/** Beginn des lokalen Tages. */
export function startOfLocalDay(now: Date): Date {
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  return start;
}

/**
 * Ein Termin ist überfällig, wenn er vor dem heutigen Tag lag. Bewusst tagesgenau
 * und nicht minutengenau: ein Transport um 07:00, den man um 07:30 anschaut, ist
 * nicht „überfällig" — er läuft gerade.
 */
export function isOverdue(plannedAt: string | null, now: Date): boolean {
  if (!plannedAt) return false;
  return new Date(plannedAt).getTime() < startOfLocalDay(now).getTime();
}

/**
 * Anstehende Transporte für das Widget: überfällige zuerst, dann die heutigen nach
 * Uhrzeit. Ohne Termin taucht hier nichts auf — was nicht terminiert ist, steht
 * nicht an.
 */
export function sortSchedule(transports: TransportListItem[], now: Date): TransportListItem[] {
  return transports
    .filter((transport) => transport.plannedAt !== null)
    .sort((left, right) => {
      const leftOverdue = isOverdue(left.plannedAt, now);
      const rightOverdue = isOverdue(right.plannedAt, now);
      if (leftOverdue !== rightOverdue) return leftOverdue ? -1 : 1;
      return new Date(left.plannedAt as string).getTime() - new Date(right.plannedAt as string).getTime();
    });
}

/** Zählung für die Kopfzeile des Widgets. */
export function countByDirection(transports: TransportListItem[]): { inbound: number; outbound: number } {
  return {
    inbound: transports.filter((transport) => transport.direction === 'INBOUND').length,
    outbound: transports.filter((transport) => transport.direction === 'OUTBOUND').length,
  };
}
